"use client";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./LiveLecture1011.css";

import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getDay,
  isValid,
  parse,
  startOfMonth,
  startOfToday,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { type FC, useEffect, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";

import { getEventOptions, getEvents } from "@/api/liveLecture";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/atoms/Select";
import { Button } from "@/components/atoms/Button";
import { cn, formatSectionTitleWithColor } from "@/lib/utils";
import type {
  Event,
  EventDetailsResponse,
  EventFormOptions,
  Filters,
  LiveLectureProps,
} from "@/types/liveLecture";

const CALENDAR_VIEWS = {
  MONTH: "month",
  DAY: "day",
};

const NAVIGATE = {
  NEXT: "NEXT",
  PREV: "PREV",
};

/**
 * Chandigarh reference palette. These back the CMS colour fields so an
 * unconfigured section still renders exactly like the CU portal.
 */
const CU = {
  BACKGROUND: "#FFFFFF",
  TEXT: "#171719",
  DAY_SECTION: "#FFFFFF",
  VIEW_BUTTON_BG: "#F3F4F6",
  VIEW_BUTTON_ACTIVE_BG: "#B8B8B8",
  NAVIGATE: "#171719",
  BUTTON_BG: "#E1473F",
  BUTTON_TEXT: "#FFFFFF",
  EVENT: "#848484",
  EVENT_TEXT: "#FFFFFF",
  TOOLTIP: "#991B1B",
  TOOLTIP_TEXT: "#FFFFFF",
};

/** Hardcoded in the reference component, so not admin-driven. */
const SELECT_BORDER = "#E6E6E6";
const CALENDAR_BORDER = "#5C5C5C";
const EVENT_HOVER = "#BEBEBE";
const TOOLBAR_BUTTON_BORDER = "#b8b8b8";

const locales = {
  "en-US": require("date-fns/locale/en-US"),
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

const LiveLecture1011: FC<LiveLectureProps> = ({
  data,
  handleTooltipContent: externalHandleTooltipContent,
  handleSelectedEventId: externalHandleSelectedEventId,
  tooltipContent: externalTooltipContent,
  selectedEventId: externalSelectedEventId,
}) => {
  const stylingData = data?.data ?? data ?? {};
  const {
    title,
    title_text_color,
    title_highlight_color,
    component_background_color,
    day_section_color,
    day_text_color,
    view_button_text_color,
    view_button_bg_color,
    view_button_active_bg_color,
    view_button_active_text_color,
    active_bar_color,
    navigate_button_color,
    left_navigate_icon,
    right_navigate_icon,
    button_icon,
    button_text,
    button_text_color,
    button_bg_color,
    event_color,
    event_text_color,
    tooltip_color,
    tooltip_text_color,
    description,
    content,
  } = stylingData as NonNullable<NonNullable<LiveLectureProps["data"]>["data"]>;

  const [filters, setFilters] = useState<Filters>({
    courseName: "",
    semYear: "",
    batch: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    courseName: "",
    semYear: "",
    batch: "",
  });
  const [view, setView] = useState<string>(CALENDAR_VIEWS.MONTH);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [eventFormOptions, setEventFormOptions] = useState<EventFormOptions>(
    {} as EventFormOptions
  );
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // The reference component is always driven by a parent that owns the tooltip.
  // Rendered as a CMS section there is no such parent, so fall back to local
  // state and only defer to the props when they are actually supplied.
  const [internalTooltipContent, setInternalTooltipContent] = useState<
    EventDetailsResponse | Record<string, unknown>
  >({});
  const [internalSelectedEventId, setInternalSelectedEventId] = useState("");

  const tooltipContent = externalTooltipContent ?? internalTooltipContent;
  const selectedEventId = externalSelectedEventId ?? internalSelectedEventId;

  const handleTooltipContent = (
    nextContent: EventDetailsResponse | Record<string, unknown>
  ) => {
    if (externalHandleTooltipContent) {
      externalHandleTooltipContent(nextContent);
      return;
    }
    setInternalTooltipContent(nextContent);
  };

  const handleSelectedEventId = (id: string) => {
    if (externalHandleSelectedEventId) {
      externalHandleSelectedEventId(id);
      return;
    }
    setInternalSelectedEventId(id);
  };

  const getStartAndEndDates = (date: Date) => {
    const startDate = startOfMonth(date);
    const endDate = endOfMonth(date);
    const customStartDate = subDays(startDate, 15);
    const customEndDate = addDays(endDate, 15);
    const formattedStartDate = format(customStartDate, "yyyy-MM-dd");
    const formattedEndDate = format(customEndDate, "yyyy-MM-dd");
    return { formattedStartDate, formattedEndDate };
  };

  const fetchEventsForRange = async (startDate: string, endDate: string) => {
    try {
      const eventData = await getEvents(startDate, endDate);
      return Array.isArray(eventData) ? eventData : [];
    } catch {
      return [];
    }
  };

  const isObjectEmpty = (obj: Record<string, unknown>) =>
    Object.keys(obj).length === 0;

  const hideTooltip = () => {
    handleTooltipContent({});
    handleSelectedEventId("");
  };

  // Hide the tooltip on scroll, but only while one is actually showing.
  useEffect(() => {
    const handleScroll = () => {
      if (
        !isObjectEmpty(tooltipContent as Record<string, unknown>) ||
        selectedEventId
      ) {
        hideTooltip();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [tooltipContent, selectedEventId]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const eventOptions = await getEventOptions();
      setEventFormOptions(eventOptions || {});
      const today = new Date();
      const { formattedStartDate, formattedEndDate } =
        getStartAndEndDates(today);
      const eventData = await fetchEventsForRange(
        formattedStartDate,
        formattedEndDate
      );
      setEvents(eventData);
      setFilteredEvents(eventData);
    };
    fetchInitialData();
  }, []);

  const handleFilterChange = (filterName: string, value: string) => {
    setFilters((prevFilters: Filters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
  };

  const handleSubmit = (allEvents: Event[] = [], isSubmitted = false) => {
    const eventsData = isSubmitted ? allEvents : events;
    const updatedFilters = isSubmitted ? appliedFilters : filters;
    if (!isSubmitted) setAppliedFilters(filters);

    const validFilterKeys = Object.keys(updatedFilters).filter(
      (key) =>
        updatedFilters[key as keyof Filters] !== "" &&
        updatedFilters[key as keyof Filters] !== "SELECT"
    ) as (keyof Filters)[];
    if (validFilterKeys.length === 0) {
      return setFilteredEvents(eventsData);
    }

    const filteredValues = eventsData.filter((event) => {
      const { eventDetails } = event;
      return validFilterKeys.every(
        (key) => eventDetails?.[key] === updatedFilters[key]
      );
    });

    setFilteredEvents(filteredValues);
  };

  const handleViewChange = async (newView: string) => {
    if (newView === CALENDAR_VIEWS.DAY) {
      const date = new Date(currentDate);
      setCurrentDate(startOfMonth(date));
    }
    setView(newView);
    const { formattedStartDate, formattedEndDate } =
      getStartAndEndDates(currentDate);
    const eventData = await fetchEventsForRange(
      formattedStartDate,
      formattedEndDate
    );
    setEvents(eventData);
    return handleSubmit(eventData, true);
  };

  const getNavigateByDate = (
    currentView: string
  ): Record<string, (date: Date) => Date> => {
    const navigateByDay = {
      PREV: (date: Date) => addDays(date, -1),
      NEXT: (date: Date) => addDays(date, 1),
      TODAY: () => startOfToday(),
    };

    const navigateByMonth = {
      PREV: (date: Date) => subMonths(date, 1),
      NEXT: (date: Date) => addMonths(date, 1),
      TODAY: () => startOfToday(),
    };

    return currentView === CALENDAR_VIEWS.DAY ? navigateByDay : navigateByMonth;
  };

  const handleNavigate = async (action: string) => {
    const dateManipulation = getNavigateByDate(view);
    const newDate = dateManipulation[action]
      ? dateManipulation[action](currentDate)
      : currentDate;

    setCurrentDate(newDate);
    const { formattedStartDate, formattedEndDate } =
      getStartAndEndDates(newDate);
    const eventData = await fetchEventsForRange(
      formattedStartDate,
      formattedEndDate
    );
    setEvents(eventData);
    return handleSubmit(eventData, true);
  };

  const handleEventHover = (
    _e: React.MouseEvent,
    event: Event = {} as Event
  ) => {
    if (isTouchDevice) return;
    if (event.eventDetails) {
      handleTooltipContent(event.eventDetails);
      return;
    }
    handleTooltipContent({});
  };

  const handleEventMove = (e: React.MouseEvent) => {
    if (isTouchDevice) return;
    const currentX = e.clientX;
    const currentY = e.clientY;
    if (currentX !== lastMousePosition.x && currentY !== lastMousePosition.y) {
      setTooltipPosition({ x: currentX, y: currentY });
      setLastMousePosition({ x: currentX, y: currentY });
    }
  };

  const handleEventLeave = () => {
    if (isTouchDevice) return;
    hideTooltip();
  };

  const handleTouchStart = (event: Event, e: React.TouchEvent) => {
    if (!isTouchDevice) setIsTouchDevice(true);

    const touchX = e.touches?.[0]?.clientX ?? 0;
    const touchY = e.touches?.[0]?.clientY ?? 0;
    const screenWidth = window.innerWidth;
    const isRightSide = touchX > screenWidth * 0.5;

    setTooltipPosition({
      x: isRightSide ? touchX - 250 : touchX + 10,
      y: touchY - 10,
    });

    if (event.eventDetails) {
      handleTooltipContent(event.eventDetails);
    }
  };

  const handleTouchEnd = () => {
    hideTooltip();
  };

  const handleCalendarMouseLeave = () => {
    hideTooltip();
  };

  const getFormattedTime = (time?: string) => {
    if (!time) return "N/A";
    const date = parse(time, "HH:mm:ss", new Date());
    return isValid(date) ? format(date, "hh:mma") : "N/A";
  };

  const getFormattedDate = (date?: string | Date) => {
    if (!date) return "N/A";
    const parsed = date instanceof Date ? date : new Date(date);
    return isValid(parsed) ? format(parsed, "dd/MM/yyyy") : "N/A";
  };

  const details = tooltipContent as Partial<EventDetailsResponse>;

  const selectTriggerClassName = cn(
    "flex items-center justify-between",
    "h-[40px] w-[328px] gap-[5px] px-3 text-[16px] font-normal normal-case leading-normal",
    "sm:w-full rounded-sm border bg-white cursor-pointer"
  );

  // `contentDefault`/`itemDefault` in the Select atom are empty strings, so the
  // dropdown has no background or padding of its own — it must be styled here
  // or the options render unreadably over the page. z-[120] clears the admin
  // preview Dialog's z-[110] overlay.
  const selectContentClassName = cn(
    "z-[120] w-[var(--radix-select-trigger-width)] overflow-hidden",
    "rounded-md border border-[#E6E6E6] bg-white shadow-lg"
  );

  const selectViewportClassName =
    "max-h-[300px] min-h-0 overflow-y-auto p-1";

  const selectItemClassName = cn(
    "relative cursor-pointer select-none rounded-sm px-3 py-2 text-[16px]",
    "text-[#171719] outline-none hover:bg-[#F3F4F6] focus:bg-[#F3F4F6]",
    "data-[state=checked]:font-medium"
  );

  return (
    <div
      className="w-full"
      onClick={handleTouchEnd}
      onMouseOver={!isTouchDevice ? handleEventLeave : undefined}
      style={{ backgroundColor: component_background_color || CU.BACKGROUND }}
    >
      {/* Centred content band. The reference page wraps the component in a
          1114px container and the component adds 56px of its own padding, so
          the calendar sits ~255px in from each edge at 1512px. The CMS shell
          provides no container of its own — each widget owns one. */}
      <div className="mx-auto w-full max-w-[1114px] md:px-[56px]">
      {title && (
        <h2
          className="col-span-2 mt-[38px] px-[14px] text-heading-5-semibold md:pl-[10px] md:text-heading-4-semibold"
          style={{ color: title_text_color || CU.TEXT }}
          dangerouslySetInnerHTML={{
            __html: formatSectionTitleWithColor(
              title,
              title_highlight_color,
              title_text_color || CU.TEXT
            ),
          }}
        />
      )}

      {(content || data?.content) && (
        <div
          dangerouslySetInnerHTML={{
            __html: content || data?.content || "",
          }}
          className="col-span-2 mt-[38px] px-[14px] pl-[14px] md:pl-[10px]"
        />
      )}

      <div className="mt-[25px] flex flex-col items-start justify-center gap-[25px] px-[14px] sm:max-w-[790px] sm:flex-row sm:justify-start md:px-[10px]">
        <Select
          onValueChange={(value: string) =>
            handleFilterChange("courseName", value)
          }
        >
          <SelectTrigger
            className={cn(selectTriggerClassName, "sm:shrink")}
            style={{ borderColor: SELECT_BORDER, color: CU.TEXT }}
          >
            <SelectValue placeholder="Select Course Name" />
          </SelectTrigger>
          <SelectContent
            className={selectContentClassName}
            viewportClassName={selectViewportClassName}
          >
            <SelectGroup>
              <SelectItem value="SELECT" className={selectItemClassName}>
                Select Course Name
              </SelectItem>
              {eventFormOptions?.courseName?.map((course: string) => (
                <SelectItem
                  key={course}
                  value={course}
                  className={selectItemClassName}
                >
                  {course}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        <Select onValueChange={(value) => handleFilterChange("semYear", value)}>
          <SelectTrigger
            className={selectTriggerClassName}
            style={{ borderColor: SELECT_BORDER, color: CU.TEXT }}
          >
            <SelectValue placeholder="Select Semester" />
          </SelectTrigger>
          <SelectContent
            className={selectContentClassName}
            viewportClassName={selectViewportClassName}
          >
            <SelectGroup>
              <SelectItem value="SELECT" className={selectItemClassName}>
                Select Semester
              </SelectItem>
              {eventFormOptions?.semYear?.map((sem: string) => (
                <SelectItem
                  key={sem}
                  value={sem}
                  className={selectItemClassName}
                >
                  {sem}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        <Select onValueChange={(value) => handleFilterChange("batch", value)}>
          <SelectTrigger
            className={selectTriggerClassName}
            style={{ borderColor: SELECT_BORDER, color: CU.TEXT }}
          >
            <SelectValue placeholder="Select Batch" />
          </SelectTrigger>
          <SelectContent
            className={selectContentClassName}
            viewportClassName={selectViewportClassName}
          >
            <SelectGroup>
              <SelectItem value="SELECT" className={selectItemClassName}>
                Select Batch
              </SelectItem>
              {eventFormOptions?.batch?.map((value: string) => (
                <SelectItem
                  key={value}
                  value={value}
                  className={selectItemClassName}
                >
                  {value}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        <Button
          className="flex h-[40px] w-[328px] items-center justify-center gap-[7px] text-[16px] font-medium normal-case leading-normal transition-opacity active:opacity-70 sm:w-full"
          onClick={() => handleSubmit()}
          style={{
            backgroundColor: button_bg_color || CU.BUTTON_BG,
            color: button_text_color || CU.BUTTON_TEXT,
          }}
        >
          {button_text || "Submit"}
          {button_icon && (
            <img src={button_icon} alt="" className="size-6" aria-hidden />
          )}
        </Button>
      </div>

      <div
        className="mt-[24px] mb-[32px] h-[320px] w-full px-[24px] pb-0 sm:border sm:px-[10px] md:mt-14 md:mb-14 md:h-[640px]"
        style={{ borderColor: CALENDAR_BORDER }}
        onMouseLeave={handleCalendarMouseLeave}
        onClick={handleTouchEnd}
      >
        {!isObjectEmpty(tooltipContent as Record<string, unknown>) && (
          <div className="relative">
            <div
              className={cn(
                selectedEventId ? "absolute w-[200px]" : "fixed w-[250px]",
                "z-[45] rounded-md px-3 py-2 text-sm shadow-md"
              )}
              style={{
                backgroundColor: tooltip_color || CU.TOOLTIP,
                color: tooltip_text_color || CU.TOOLTIP_TEXT,
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: "1.4",
                pointerEvents: "none",
                transform: selectedEventId
                  ? "translate(-10%, -120%)"
                  : "translate(10%, -10%)",
              }}
            >
              <p>Course:- {details?.courseName}</p>
              <p>Subject:- {details?.subjectName}</p>
              <p>Date:- {getFormattedDate(details?.startDate)}</p>
              <p>
                Time:- {getFormattedTime(details?.startTime)} to{" "}
                {getFormattedTime(details?.endTime)}
              </p>
              <p>Batch:- {details?.batch}</p>
              <p>Semester:- {details?.semYear}</p>
            </div>
          </div>
        )}

        <Calendar
          localizer={localizer}
          events={filteredEvents}
          selectable={false}
          startAccessor="start"
          endAccessor="end"
          views={[CALENDAR_VIEWS.MONTH, CALENDAR_VIEWS.DAY]}
          view={view}
          onView={handleViewChange}
          date={currentDate}
          dayPropGetter={() => ({
            className: view === CALENDAR_VIEWS.MONTH ? "bg-transparent" : "",
          })}
          showAllEvents
          eventPropGetter={() => ({
            className: "text-[12px] overflow-hidden !p-0",
          })}
          components={{
            timeGutterHeader: () => (
              <div className="flex items-center">All-Day</div>
            ),
            header: ({ date, label }: { date: Date; label?: string }) => (
              <div
                className="flex w-full items-center justify-center py-2 font-medium"
                style={{
                  backgroundColor: day_section_color || CU.DAY_SECTION,
                  color: day_text_color || CU.TEXT,
                }}
              >
                {view === CALENDAR_VIEWS.DAY
                  ? format(date, "EEEE")
                  : label || format(date, "EEE")}
              </div>
            ),
            toolbar: ({ date }: { date: Date }) => (
              <div
                className="flex items-center justify-between pt-[10px] pb-[10px] md:py-[20px]"
                onMouseOver={(e) => e.stopPropagation()}
                onMouseMove={(e) => {
                  e.stopPropagation();
                  if (!isObjectEmpty(tooltipContent as Record<string, unknown>))
                    handleEventLeave();
                }}
              >
                <div className="flex">
                  <button
                    type="button"
                    className="flex h-6 w-[29px] items-center justify-center rounded-l-[2px] border border-r-0 text-[22px] hover:bg-[#eaecf0] sm:w-14 md:h-[30px] md:rounded-l-md md:bg-[#f3f4f6]"
                    style={{
                      borderColor: TOOLBAR_BUTTON_BORDER,
                      color: navigate_button_color || CU.NAVIGATE,
                    }}
                    onClick={() => handleNavigate(NAVIGATE.PREV)}
                    aria-label="Previous"
                  >
                    {left_navigate_icon ? (
                      <img src={left_navigate_icon} alt="" className="size-5" />
                    ) : (
                      <span>&#60;</span>
                    )}
                  </button>

                  <button
                    type="button"
                    className="flex h-6 w-[29px] items-center justify-center rounded-r-[2px] border text-[22px] hover:bg-[#eaecf0] sm:w-14 md:h-[30px] md:rounded-r-md md:bg-[#f3f4f6]"
                    style={{
                      borderColor: TOOLBAR_BUTTON_BORDER,
                      color: navigate_button_color || CU.NAVIGATE,
                    }}
                    onClick={() => handleNavigate(NAVIGATE.NEXT)}
                    aria-label="Next"
                  >
                    {right_navigate_icon ? (
                      <img
                        src={right_navigate_icon}
                        alt=""
                        className="size-5"
                      />
                    ) : (
                      <span>&#62;</span>
                    )}
                  </button>
                </div>

                <span
                  className="text-[14px] font-medium sm:text-[25px]"
                  style={{ color: day_text_color || CU.TEXT }}
                >
                  {view === CALENDAR_VIEWS.DAY
                    ? format(date, "MMMM dd, yyyy")
                    : format(date, "MMMM yyyy")}
                </span>

                <div className="flex">
                  <button
                    type="button"
                    className="h-6 w-14 rounded-l-md text-[14px] md:h-[30px]"
                    onClick={() => handleViewChange(CALENDAR_VIEWS.MONTH)}
                    style={{
                      backgroundColor:
                        view === CALENDAR_VIEWS.MONTH
                          ? view_button_active_bg_color ||
                            CU.VIEW_BUTTON_ACTIVE_BG
                          : view_button_bg_color || CU.VIEW_BUTTON_BG,
                      color:
                        view === CALENDAR_VIEWS.MONTH
                          ? view_button_active_text_color || CU.TEXT
                          : view_button_text_color || CU.TEXT,
                      borderBottom:
                        view === CALENDAR_VIEWS.MONTH
                          ? `2px solid ${
                              active_bar_color || CU.VIEW_BUTTON_ACTIVE_BG
                            }`
                          : undefined,
                    }}
                  >
                    Month
                  </button>

                  <button
                    type="button"
                    className="h-6 w-14 rounded-r-md text-[14px] md:h-[30px]"
                    onClick={() => handleViewChange(CALENDAR_VIEWS.DAY)}
                    style={{
                      backgroundColor:
                        view === CALENDAR_VIEWS.DAY
                          ? view_button_active_bg_color ||
                            CU.VIEW_BUTTON_ACTIVE_BG
                          : view_button_bg_color || CU.VIEW_BUTTON_BG,
                      color:
                        view === CALENDAR_VIEWS.DAY
                          ? view_button_active_text_color || CU.TEXT
                          : view_button_text_color || CU.TEXT,
                      borderBottom:
                        view === CALENDAR_VIEWS.DAY
                          ? `2px solid ${
                              active_bar_color || CU.VIEW_BUTTON_ACTIVE_BG
                            }`
                          : undefined,
                    }}
                  >
                    Day
                  </button>
                </div>
              </div>
            ),
            event: ({ event }: { event: any }) => {
              const typedEvent = event as Event;
              const isStartValid =
                typedEvent?.start && isValid(typedEvent.start);
              const formattedStart = isStartValid
                ? format(typedEvent.start, "hh:mma")
                : "";
              const eventTitle = typedEvent?.title || "";
              const stableEventId = typedEvent.eventDetails?.id || eventTitle;
              const isActive = Boolean(
                stableEventId && selectedEventId === stableEventId
              );

              return (
                <div
                  id={stableEventId}
                  onMouseOver={
                    !isTouchDevice
                      ? (e) => {
                          e.stopPropagation();
                          handleEventHover(e, typedEvent);
                        }
                      : undefined
                  }
                  onMouseMove={
                    !isTouchDevice ? (e) => handleEventMove(e) : undefined
                  }
                  onMouseLeave={!isTouchDevice ? handleEventLeave : undefined}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleTouchStart(typedEvent, e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="h-full p-1 transition-colors"
                  style={{
                    backgroundColor: isActive
                      ? EVENT_HOVER
                      : event_color || CU.EVENT,
                    color: event_text_color || CU.EVENT_TEXT,
                  }}
                  onMouseEnter={
                    !isTouchDevice
                      ? (e) => {
                          e.currentTarget.style.backgroundColor = EVENT_HOVER;
                        }
                      : undefined
                  }
                  onMouseOut={
                    !isTouchDevice
                      ? (e) => {
                          e.currentTarget.style.backgroundColor = isActive
                            ? EVENT_HOVER
                            : event_color || CU.EVENT;
                        }
                      : undefined
                  }
                >
                  {`${formattedStart} ${eventTitle}`}
                </div>
              );
            },
          }}
        />

        {description ? (
          <div
            className="text-[14px] leading-normal"
            style={{ color: "#000000" }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        ) : null}
        </div>
      </div>
    </div>
  );
};

export default LiveLecture1011;
